import { MUTATION, useMutationOperation } from 'api/Mutation';
import type { UnresolvedCommitDescriptor } from 'custom-types/UnresolvedCommitDescriptor';
import type { JSX, KeyboardEvent } from 'react';
import { useState } from 'react';
import { useKeyboardShortcut } from 'ts/base/hooks/UseKeyboardShortcut';
import { useProjectId } from 'ts/base/hooks/UseProject';
import { KeyboardShortcutRegistry } from 'ts/base/scaffolding/KeyboardShortcutRegistry';
import { useSuspenseServerOption } from 'ts/commons/hooks/UseServerOption';
import { StringUtils } from 'ts/commons/StringUtils';
import { UIUtils } from 'ts/commons/UIUtils';
import { Button } from 'ts/components/Button';
import { Form, FormField } from 'ts/components/Form';
import { Message } from 'ts/components/Message';
import { Modal, ModalActionButtons } from 'ts/components/Modal';
import { TextArea } from 'ts/components/TextArea';
import { EServerOptions } from 'typedefs/ServerOptions';

type ResolvePendingExclusionRationaleModalProps = {
	findingIds: string[];
	/** The commit that is used to flag findings. */
	commit: UnresolvedCommitDescriptor | null;
	approve: boolean;
	onClose: () => void;
	onSuccess?: () => void;
};

/** Renders modal for entering a blacklisting rationale. */
export function ResolvePendingExclusionRationaleModal({
	findingIds,
	approve,
	commit,
	onClose,
	onSuccess
}: ResolvePendingExclusionRationaleModalProps): JSX.Element {
	const [rationale, setRationale] = useState<string>('');
	const project = useProjectId();

	const mutation = useMutationOperation(MUTATION.resolvePendingFindingsExclusion);

	const { disableEmptyRationale } = useSuspenseServerOption(EServerOptions.BLACKLISTING);
	const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);

	const onSubmit = () => {
		if (disableEmptyRationale && StringUtils.isEmptyOrWhitespace(rationale)) {
			setShowErrorMessage(true);
			return;
		}
		setShowErrorMessage(false);
		mutation.mutate(
			{
				pathParams: { project },
				queryParams: {
					t: commit ?? undefined
				},
				body: {
					approve,
					findingIds,
					rationale
				}
			},
			{
				onSuccess: () => onSuccess?.(),
				onSettled: () => onClose()
			}
		);
	};

	useKeyboardShortcut(
		`${KeyboardShortcutRegistry.CMD_CTRL} ENTER`,
		`Saving pending exclusion ${approve ? 'approval' : 'rejection'} rationale`,
		onSubmit
	);

	return (
		<Modal opened onClose={onClose} size="xl" title="Enter rationale" data-testid="flagging-rationale-modal">
			<Form error id="resolve-flagging-rationale-form" onSubmit={onSubmit}>
				{showErrorMessage ? <Message error content="Rationale must not be empty." /> : null}
				<FormField>
					<p>
						<label>
							Rationale for {approve ? 'approving' : 'rejecting'}{' '}
							{findingIds.length > 1 ? `these ${findingIds.length} findings'` : "this finding's"} pending
							exclusion. {disableEmptyRationale ? '' : ' (Optional)'}
						</label>
					</p>
					<TextArea
						data-autofocus
						data-testid="rationale-text"
						rows={3}
						onChange={e => setRationale(e.target.value)}
						onKeyDown={(event: KeyboardEvent) => {
							if (UIUtils.isCtrlKey(event) && event.code === 'Enter') {
								onSubmit();
							}
						}}
					/>
				</FormField>
			</Form>
			<ModalActionButtons>
				<Button
					primary
					type="submit"
					content="OK"
					className={'!w-40 ' + (mutation.isPending ? 'loading' : '')}
					data-testid="flagging-rationale-modal-ok"
					form="resolve-flagging-rationale-form"
					disabled={mutation.isPending}
				/>
				<Button content="Cancel" className="!w-40" disabled={mutation.isPending} onClick={onClose} />
			</ModalActionButtons>
		</Modal>
	);
}
